import { INVALID_MOVE } from 'boardgame.io/core';
import { debug } from '../../utils/debug';
import type { Ctx } from 'boardgame.io';
import type { AoSGameState, ProductionArgs, GoodsColor } from '../../types';

/**
 * Production アクション実行 Move
 * 仕様書: docs/specifications/logic/common/phase_09_goods_growth.md
 *
 * @param G - ゲーム状態
 * @param ctx - Boardgame.io コンテキスト
 * @param args - Production引数（2個の商品配置情報）
 * @returns void or INVALID_MOVE
 */
export function executeProduction(
  { G, ctx }: { G: AoSGameState; ctx: Ctx },
  args: ProductionArgs
): void | typeof INVALID_MOVE {
  // 1. Production選択チェック
  const player = G.players[ctx.currentPlayer];
  if (player.selectedAction !== 'PRODUCTION') {
    console.error(`[executeProduction] Production not selected, current action: ${player.selectedAction}`);
    return INVALID_MOVE;
  }

  // 2. 配置数チェック（1個ずつ配置する設計）
  if (args.placements.length !== 1) {
    console.error(`[executeProduction] Invalid placement count: ${args.placements.length}, expected: 1`);
    return INVALID_MOVE;
  }

  // 3. 各配置のバリデーション
  for (const placement of args.placements) {
    // sideチェック
    if (placement.side !== 'light' && placement.side !== 'dark') {
      console.error(`[executeProduction] Invalid side: ${placement.side}`);
      return INVALID_MOVE;
    }

    // columnチェック
    if (placement.column < 1 || placement.column > 6) {
      console.error(`[executeProduction] Invalid column: ${placement.column}`);
      return INVALID_MOVE;
    }

    // 列タイプの決定（未指定の場合は 'city' として扱う）
    const columnType = placement.columnType || 'city';

    // 列を取得（columnType によって検索方法を変更）
    const columns = placement.side === 'light' ? G.goodsDisplay.lightSide : G.goodsDisplay.darkSide;
    let column;
    if (columnType === 'newCity') {
      // 新都市列を検索（newCityId が設定されており、columnNumber が一致）
      column = columns.find(col =>
        col.newCityId !== null &&
        col.columnNumber === placement.column
      );
    } else {
      // 都市列を検索（cityId が設定されており、columnNumber が一致）
      column = columns.find(col =>
        col.cityId !== '' &&
        col.newCityId === null &&
        col.columnNumber === placement.column
      );
    }

    if (!column) {
      console.error(`[executeProduction] Column ${placement.column} (${columnType}) not found on ${placement.side} side`);
      return INVALID_MOVE;
    }

    // rowチェック（新都市列は 0-1 のみ、都市列は 0-2 のみ）
    const maxRow = columnType === 'newCity' ? 1 : 2;
    if (placement.row < 0 || placement.row > maxRow) {
      console.error(`[executeProduction] Invalid row: ${placement.row} for ${columnType} column (max: ${maxRow})`);
      return INVALID_MOVE;
    }

    // マスが空かチェック
    if (column.cubes[placement.row] !== null) {
      console.error(`[executeProduction] Cell not empty: side=${placement.side}, column=${placement.column}, row=${placement.row}`);
      return INVALID_MOVE;
    }

    // 商品色チェック
    const validColors: GoodsColor[] = ['red', 'blue', 'purple', 'yellow', 'black'];
    if (!validColors.includes(placement.color)) {
      console.error(`[executeProduction] Invalid color: ${placement.color}`);
      return INVALID_MOVE;
    }
  }

  // 4. 配置実行
  for (const placement of args.placements) {
    const columnType = placement.columnType || 'city';
    const columns = placement.side === 'light' ? G.goodsDisplay.lightSide : G.goodsDisplay.darkSide;

    // columnType によってカラムを検索
    let column;
    if (columnType === 'newCity') {
      column = columns.find(col =>
        col.newCityId !== null &&
        col.columnNumber === placement.column
      )!;
    } else {
      column = columns.find(col =>
        col.cityId !== '' &&
        col.newCityId === null &&
        col.columnNumber === placement.column
      )!;
    }

    column.cubes[placement.row] = placement.color;
    const columnLabel = columnType === 'newCity' ? column.newCityId : placement.column;
    debug.log(`[executeProduction] Placed ${placement.color} at column ${columnLabel}, row ${placement.row} (${placement.side} side, ${columnType})`);
  }

  // 5. 配置カウントをインクリメント
  if (G.productionPlacementCount === undefined) {
    G.productionPlacementCount = 0;
  }
  G.productionPlacementCount += 1;
  debug.log(`[executeProduction] Placement count: ${G.productionPlacementCount}/2`);
}
